import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { signInWithGoogle, signInWithApple } from '../../firebase';

interface SocialLoginButtonsProps {
  onLoginSuccess?: (user: any, provider: string) => void;
  onLoginError?: (error: Error) => void;
  isDisabled?: boolean;
  loadingProvider?: 'google' | 'apple' | null;
  setLoadingProvider?: (provider: 'google' | 'apple' | null) => void;
}

const SocialLoginButtons: React.FC<SocialLoginButtonsProps> = ({
  onLoginSuccess,
  onLoginError,
  isDisabled = false,
  loadingProvider = null,
  setLoadingProvider = () => {}
}) => {
  const handleGoogleSignIn = async () => {
    if (isDisabled || loadingProvider !== null) return;

    try {
      setLoadingProvider('google');
      const result = await signInWithGoogle();
      onLoginSuccess?.(result.user, result.provider);
    } catch (error: any) {
      onLoginError?.(error);
    } finally {
      setLoadingProvider(null);
    }
  };

  const handleAppleSignIn = async () => {
    if (isDisabled || loadingProvider !== null) return;

    try {
      setLoadingProvider('apple');
      const result = await signInWithApple();
      onLoginSuccess?.(result.user, result.provider);
    } catch (error: any) {
      onLoginError?.(error);
    } finally {
      setLoadingProvider(null);
    }
  };

  return (
    <div className="social-login-buttons">
      <div className="d-grid gap-2 mb-3">
        <Button
          variant="light"
          className="social-login-btn google-btn"
          onClick={handleGoogleSignIn}
          disabled={isDisabled || loadingProvider !== null}
          style={{
            border: '1px solid #dadce0',
            backgroundColor: '#ffffff',
            color: '#3c4043',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
        >
          {loadingProvider === 'google' ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              style={{ fill: '#4285F4' }}
            >
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" style={{ fill: '#34A853' }}/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" style={{ fill: '#FBBC05' }}/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" style={{ fill: '#EA4335' }}/>
            </svg>
          )}
          Continue with Google
        </Button>

        <Button
          variant="dark"
          className="social-login-btn apple-btn"
          onClick={handleAppleSignIn}
          disabled={isDisabled || loadingProvider !== null}
          style={{
            backgroundColor: '#000000',
            borderColor: '#000000',
            color: '#ffffff',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}
        >
          {loadingProvider === 'apple' ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          )}
          Continue with Apple
        </Button>
      </div>
    </div>
  );
};

export default SocialLoginButtons;